"use client";
import { useState, useEffect, useRef } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import SafeImage from "@/components/SafeImage";

const icons = {
  architecture: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M3 21h18M4 21V9l8-6 8 6v12M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  build: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 3l8 4v5c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V7l8-4z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  strategic: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 2a10 10 0 1010 10A10 10 0 0012 2zm0 18a8 8 0 118-8 8 8 0 01-8 8z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

export default function PremiumExcellenceSection({ sectionSpace = "py-5" }) {
  const [activeTab, setActiveTab] = useState(0);
  const [direction, setDirection] = useState("next");
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const AUTO_ADVANCE_MS = 6000;

  const features = [
    {
      id: "01",
      iconKey: null,
      title: "Residential Real Estate",
      heading: "Excellence in Residential Real Estate Development",
      image: "/img/imgs home/project-aayodhya.webp",
      description:
        "Siddhivinayak Group specializes in developing premium residential projects in Nagpur designed to meet the evolving needs of modern families. Every project is thoughtfully planned with a focus on comfort, functionality, aesthetics, and long-term value.",
      subDescription:
        "Our residential properties in Nagpur feature spacious layouts, efficient space utilization, natural ventilation, abundant natural light, and modern amenities designed to provide a comfortable lifestyle.",
      hasList: true,
      listTitle: "Our homes offer:",
      listItems: [
        "Comfortable and spacious living spaces",
        "Modern amenities and facilities",
        "Practical and efficient floor plans",
        "Enhanced safety features",
        "Sustainable development practices",
        "Premium lifestyle experiences",
      ],
    },
    {
      id: "02",
      iconKey: "architecture",
      title: "Architecture & Design",
      heading: "Contemporary Architecture and Innovative Design",
      image: "/img/imgs home/Hilltop Heights -16.webp",
      description:
        "Siddhivinayak Group combines contemporary architecture and innovative design to create modern residential developments that are both visually appealing and functionally efficient.",
      subDescription:
        "Our design approach focuses on modern aesthetics, smart space planning, elegant facades, energy-efficient layouts, sustainable building concepts, and future-ready infrastructure. Each residential project in Nagpur is designed to offer an ideal balance of style, convenience, and practicality.",
      hasList: false,
    },
    {
      id: "03",
      iconKey: "build",
      title: "Construction Quality",
      heading: "Superior Construction Quality",
      image: "/img/imgs home/Siddhivinayak Nagri.webp",
      description:
        "Superior construction quality is at the heart of every Siddhivinayak Group project. We maintain strict quality standards throughout planning and construction, working with experienced architects, engineers, project managers, and construction professionals.",
      hasList: true,
      listTitle: "Our quality standards include:",
      listItems: [
        "Premium construction materials",
        "Advanced engineering practices",
        "Structural durability",
        "Precision workmanship",
        "Safety and quality compliance",
        "Durable, long-lasting finishes",
      ],
    },
    {
      id: "04",
      iconKey: "strategic",
      title: "Smart Living & Investment",
      heading: "Strategic Residential Projects in Nagpur for Smart Living & Long-Term Investment",
      image: "/img/imgs home/project-residential-VI.webp",
      description:
        "Siddhivinayak Group develops residential projects in Nagpur in strategically selected locations with excellent connectivity to schools, hospitals, shopping centers, business hubs, transportation, and essential amenities. Our properties in Nagpur are designed to offer modern living, convenience, and strong potential for long-term real estate investment.",
      subDescription:
        "As Nagpur continues to grow as a leading Smart City in India, we focus on creating modern and sustainable residential communities that enhance quality of life. Every project follows a customer-centric approach, with careful attention to homebuyer needs, quality construction, and thoughtful planning.",
      thirdDescription:
        "With prime locations, superior construction, infrastructure connectivity, and future growth potential, our residential properties in Nagpur are designed to provide lasting lifestyle and investment value.",
      hasList: false,
    },
  ];

  const currentFeature = features[activeTab];

  const startAutoAdvance = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setDirection("next");
      setActiveTab((prev) => (prev + 1) % features.length);
    }, AUTO_ADVANCE_MS);
  };

  useEffect(() => {
    if (isPaused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    startAutoAdvance();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused]);

  const handleTabClick = (index) => {
    setDirection(index > activeTab ? "next" : "prev");
    setActiveTab(index);
    if (!isPaused) startAutoAdvance();
  };

  const handlePrevTab = () => {
    setDirection("prev");
    setActiveTab((prev) => (prev - 1 + features.length) % features.length);
    if (!isPaused) startAutoAdvance();
  };

  const handleNextTab = () => {
    setDirection("next");
    setActiveTab((prev) => (prev + 1) % features.length);
    if (!isPaused) startAutoAdvance();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        setDirection("next");
        setActiveTab((prev) => (prev + 1) % features.length);
        if (!isPaused) startAutoAdvance();
      } else if (e.key === "ArrowLeft") {
        setDirection("prev");
        setActiveTab((prev) => (prev - 1 + features.length) % features.length);
        if (!isPaused) startAutoAdvance();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isPaused]);

  return (
    <section
      className={`ltn__interactive-excellence-area ${sectionSpace}`}
      style={{ backgroundColor: "#FFFFFF", position: "relative" }}
    >
      <Container>
        {/* Section Header */}
        <Row className="justify-content-center text-center mb-5">
          <Col lg={10}>
            <div className="iex-kicker-wrap">
              <span className="iex-kicker">Quality & Innovation in Nagpur Real Estate.</span>
            </div>
            <h2 className="iex-section-title">What Sets Siddhivinayak Group Apart</h2>
            <p className="iex-section-desc" style={{ textAlign: "center" }}>
              Discover what sets Siddhivinayak Group apart as a trusted real estate developer in Nagpur, from superior 
              construction quality and contemporary architecture to strategic locations, customer-focused service, and 
              a commitment to modern urban living.
            </p>
          </Col>
        </Row>

        {/* Interactive Master Card */}
        <Row>
          <Col lg={12}>
            <div
              className="iex-master-card"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Left Sidebar with Image at Top & Nav Stack at Bottom */}
              <div className="iex-nav-sidebar">
                {/* Image Above the Left Side Menu — swaps with active tab */}
                <div className="iex-sidebar-image-container">
                  <SafeImage
                    key={currentFeature.id}
                    src={currentFeature.image}
                    alt={currentFeature.imageLabel || currentFeature.title}
                    width={800}
                    height={600}
                    sizes="(max-width: 991px) 100vw, 25vw"
                    loading="lazy"
                    className={`iex-sidebar-img iex-img-anim-${direction}`}
                  />
                  <div className="iex-sidebar-img-overlay">
                    <span>{currentFeature.imageLabel || currentFeature.title}</span>
                  </div>
                </div>

                {/* Navigation Stack Pushed Completely Down. The carousel
                    auto-advances and exposes prev/next buttons, so it
                    does not follow the ARIA tabs pattern (which expects
                    keyboard arrow navigation between tab children and
                    associated tabpanels). Without `role="tablist"` the
                    a11y tree stays well-formed; the visible buttons
                    remain focusable and labelled individually. */}
                <div className="iex-nav-stack" aria-label="Excellence Pillars">
                  <button
                    type="button"
                    className="iex-nav-arrow iex-nav-arrow-left"
                    onClick={handlePrevTab}
                    aria-label="Previous"
                  >
                    ‹
                  </button>

                  {features.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      aria-pressed={activeTab === index}
                      onClick={() => handleTabClick(index)}
                      className={`iex-nav-item ${
                        activeTab === index ? "active" : ""
                      }`}
                    >
                      <span className="iex-nav-text">{item.title}</span>
                      {activeTab === index && !isPaused && (
                        <div className="iex-progress-bar" style={{ animationDuration: `${AUTO_ADVANCE_MS}ms` }} />
                      )}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="iex-nav-arrow iex-nav-arrow-right"
                    onClick={handleNextTab}
                    aria-label="Next"
                  >
                    ›
                  </button>
                </div>
              </div>

              {/* Right Content Pane */}
              <div className="iex-content-pane">
                <div
                  className={`iex-content-inner iex-anim-${direction}`}
                  key={activeTab}
                >
                  {currentFeature.iconKey && (
                    <div className="iex-icon-badge">
                      {icons[currentFeature.iconKey]}
                    </div>
                  )}

                  <h3 className="iex-pane-heading">{currentFeature.heading}</h3>
                  <div className="iex-line-accent" />

                  <p className="iex-pane-body">{currentFeature.description}</p>

                  {currentFeature.subDescription && (
                    <p className="iex-pane-body">{currentFeature.subDescription}</p>
                  )}

                  {currentFeature.thirdDescription && (
                    <p className="iex-pane-body">{currentFeature.thirdDescription}</p>
                  )}

                  {currentFeature.hasList && (
                    <div className="iex-list-container mt-4">
                      <h3 className="iex-list-title">{currentFeature.listTitle}</h3>
                      <ul className="iex-custom-list">
                        {currentFeature.listItems.map((liText, i) => (
                          <li key={i}>
                            <span className="iex-list-icon">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <path d="M20 6L9 17L4 12" stroke="#e68f36" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </span>
                            <span className="iex-list-card-content">{liText}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .iex-kicker-wrap {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
          margin-top: 40px;
        }

        .iex-kicker {
          display: inline-block;
          background: rgba(156, 90, 18, 0.12);
          color: #9c5a12;
          font-size: 13px;
          font-weight: 600;
          padding: 8px 22px;
          border-radius: 999px;
          letter-spacing: 0.3px;
        }

        .iex-section-title {
          font-size: 36px;
          font-weight: 700;
          color: #1b2422;
          margin-bottom: 14px;
          letter-spacing: -0.5px;
        }

        .iex-section-desc {
          font-size: 15.5px;
          color: #64748b;
          max-width: 1100px;
          width: 100%;
          text-align: center !important;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* Master Container Styling */
        .iex-master-card {
          display: grid;
          grid-template-columns: 310px 1fr;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 30px 60px -15px rgba(27, 36, 34, 0.09);
          min-height: 580px;
          max-width: 100%;
        }

        /* Left Sidebar Styling: Image at Top, Nav Stack Pushed Down */
        .iex-nav-sidebar {
          background: #f8fafc;
          border-right: 1px solid #e2e8f0;
          padding: 20px 18px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          width: 100%;
          box-sizing: border-box;
        }

        .iex-sidebar-image-container {
          position: relative;
          width: 100%;
          height: 280px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
          border: 1px solid #e2e8f0;
        }

        .iex-sidebar-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .iex-sidebar-image-container:hover .iex-sidebar-img {
          transform: scale(1.05);
        }

        /* Image swap animation — mirrors content pane transition */
        .iex-img-anim-next {
          animation: iex-img-fade 0.45s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .iex-img-anim-prev {
          animation: iex-img-fade 0.45s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes iex-img-fade {
          from {
            opacity: 0;
            transform: scale(1.04);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .iex-sidebar-img-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 12px 14px;
          background: linear-gradient(to top, rgba(27, 36, 34, 0.85), transparent);
          color: #ffffff;
          font-size: 12.5px;
          font-weight: 600;
          letter-spacing: 0.3px;
        }

        .iex-nav-stack {
          display: flex;
          flex-direction: column;
          gap: 10px;
          width: 100%;
          margin-top: 20px;
        }

        .iex-nav-item {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
          padding: 13px 16px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-align: left;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          overflow: hidden;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.01);
        }

        .iex-nav-text {
          font-size: 13.5px;
          font-weight: 500;
          color: #475569;
          transition: color 0.25s ease;
          line-height: 1.35;
          width: 100%;
        }

        .iex-nav-item:hover {
          border-color: rgba(230, 143, 54, 0.4);
          background: #fffbf7;
          transform: translateX(2px);
        }

        .iex-nav-item:hover .iex-nav-text {
          color: #1b2422;
        }

        /* Active Tab Styling */
        .iex-nav-item.active {
          background: #1b2422;
          border-color: #1b2422;
          box-shadow: 0 8px 20px -4px rgba(27, 36, 34, 0.2);
        }

        .iex-nav-item.active .iex-nav-text {
          color: #ffffff;
          font-weight: 600;
        }

        /* Prev/Next arrows — hidden on desktop, shown only on mobile stacked view */
        .iex-nav-arrow {
          display: none;
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #1b2422;
          font-size: 20px;
          line-height: 1;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .iex-nav-arrow:hover {
          background: #1b2422;
          border-color: #1b2422;
          color: #ffffff;
        }

        /* Auto-Advance Progress Tracker Line */
        .iex-progress-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: #e68f36;
          width: 100%;
          transform-origin: left;
          animation: iex-progress linear infinite;
        }

        @keyframes iex-progress {
          0% {
            transform: scaleX(0);
          }
          100% {
            transform: scaleX(1);
          }
        }

        /* Right Content Pane */
        .iex-content-pane {
          position: relative;
          padding: 50px;
          background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow: hidden;
          min-width: 0;
          width: 100%;
          box-sizing: border-box;
        }

        .iex-icon-badge {
          width: 58px;
          height: 58px;
          border-radius: 16px;
          background: linear-gradient(135deg, rgba(230, 143, 54, 0.16) 0%, rgba(230, 143, 54, 0.04) 100%);
          border: 1.5px solid rgba(230, 143, 54, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          color: #e68f36;
          animation: iex-icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 10px 25px -4px rgba(230, 143, 54, 0.15);
        }

        @keyframes iex-icon-pop {
          from {
            opacity: 0;
            transform: scale(0.6);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .iex-content-inner {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 100%;
          overflow-wrap: break-word;
          word-break: break-word;
        }

        .iex-anim-next {
          animation: iex-slide-in-next 0.45s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .iex-anim-prev {
          animation: iex-slide-in-prev 0.45s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes iex-slide-in-next {
          from {
            opacity: 0;
            transform: translateX(18px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes iex-slide-in-prev {
          from {
            opacity: 0;
            transform: translateX(-18px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        .iex-pane-heading {
          font-size: 26px;
          font-weight: 400;
          color: #1b2422;
          margin-bottom: 12px;
          line-height: 1.25;
          letter-spacing: -0.4px;
          overflow-wrap: anywhere;
          word-break: break-word;
          max-width: 100%;
        }

        .iex-line-accent {
          width: 48px;
          height: 3px;
          background-color: #e68f36;
          margin-bottom: 20px;
          border-radius: 2px;
        }

        .iex-pane-body {
          font-size: 15px;
          line-height: 1.75;
          color: #475569;
          margin-bottom: 14px;
          overflow-wrap: anywhere;
          word-break: break-word;
          max-width: 100%;
        }

        .iex-list-title {
          font-size: 12.5px;
          font-weight: 700;
          color: #1b2422;
          margin-bottom: 14px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        /* Enhanced Microcards Styling */
        .iex-custom-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        .iex-custom-list li {
          position: relative;
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 14px 16px 14px 50px;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
        }

        .iex-custom-list li:hover {
          border-color: #e68f36;
          background: linear-gradient(135deg, #ffffff 0%, #fffbf7 100%);
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -5px rgba(230, 143, 54, 0.12);
        }

        .iex-list-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          width: 24px;
          height: 24px;
          border-radius: 6px;
          background: rgba(230, 143, 54, 0.12);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .iex-custom-list li:hover .iex-list-icon {
          background: #e68f36;
        }

        .iex-custom-list li:hover .iex-list-icon svg path {
          stroke: #ffffff;
        }

        .iex-list-card-content {
          font-size: 14px;
          color: #334155;
          font-weight: 500;
          line-height: 1.4;
          display: block;
          transition: color 0.3s ease;
        }

        .iex-custom-list li:hover .iex-list-card-content {
          color: #1b2422;
        }

        /* Responsive Design */
        @media (max-width: 991px) {
          .iex-master-card {
            grid-template-columns: minmax(0, 1fr);
            min-height: auto;
          }
          .iex-nav-sidebar {
            flex-direction: column;
            border-right: none;
            border-bottom: 1px solid #e2e8f0;
            padding: 16px;
          }
          .iex-sidebar-image-container {
            height: 220px;
            margin-bottom: 16px;
          }
          .iex-nav-stack {
            flex-direction: row;
            align-items: center;
            overflow-x: hidden;
            gap: 8px;
            margin-top: 0;
            padding-bottom: 4px;
          }
          .iex-nav-item {
            padding: 10px 14px;
            white-space: nowrap;
            width: auto;
            border-radius: 8px;
            flex: 1 1 auto;
          }
          .iex-nav-item:not(.active) {
            display: none;
          }
          .iex-nav-arrow {
            display: flex;
          }
          .iex-progress-bar {
            display: none;
          }
          .iex-content-pane {
            padding: 30px 24px;
          }
        }

        @media (max-width: 767px) {
          .iex-section-title {
            font-size: 26px;
            line-height: 1.3;
            word-wrap: break-word;
          }
          .iex-section-desc {
            font-size: 14px;
            padding: 0 8px;
          }
          .iex-kicker {
            font-size: 11.5px;
            padding: 6px 16px;
            text-align: center;
            white-space: normal;
          }
          .iex-pane-heading {
            font-size: 20px;
            line-height: 1.35;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          .iex-pane-body {
            font-size: 14px;
            line-height: 1.65;
          }
          .iex-content-pane {
            padding: 24px 18px;
          }
          .iex-nav-text {
            font-size: 12.5px;
          }
          .iex-list-card-content {
            font-size: 13px;
            word-wrap: break-word;
          }
          .iex-custom-list li {
            padding: 12px 14px 12px 44px;
          }
        }

        @media (max-width: 575px) {
          .iex-custom-list {
            grid-template-columns: 1fr;
          }
          .iex-section-title {
            font-size: 22px;
          }
          .iex-sidebar-image-container {
            height: 180px;
          }
          .iex-nav-item {
            padding: 9px 12px;
          }
          .iex-icon-badge {
            width: 48px;
            height: 48px;
            border-radius: 12px;
          }
        }
      `}</style>
    </section>
  );
}